import React, { useState } from 'react';
import { Form, Input, Button } from 'antd';
import { useTranslation } from 'react-i18next';

import { useSubmitCompanyFormMutation } from '@Entities/signup/api/signupSlice';
import { ga4event } from '@Shared/utils/ga4event';
import { signupEvent } from '@Shared/utils/posthog';
import useLocalStorage from '@Shared/utils/useLocalStorage';

import { FieldHint, RequiredLabel, useFieldPointer, useMinLength } from './fieldHelpers';

type CompanyFormDataType = {
    companyName: string;
    companyLink: string;
    subdomain: string;
};

export default function CompanyForm({ handleStep }: { handleStep: (arg1: number) => void }) {
    const storage = useLocalStorage();
    const { t } = useTranslation('translation', { keyPrefix: 'signup' });
    const [submitCompanyForm, { isLoading }] = useSubmitCompanyFormMutation();
    const [errors, setErrors] = useState<Partial<CompanyFormDataType>>({});
    const [form] = Form.useForm();
    const { pointAt, shakeClass } = useFieldPointer(form);
    const address = useMinLength(3);
    // The server checks the name and website made from the address too; whatever it says shows under the address.
    const serverError = errors?.subdomain || errors?.companyName || errors?.companyLink;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = e.target?.id?.replace('company-form_', '');
        ga4event({
            event_name: `enter_${fieldName}`,
            event_option1: 'advertiser registration funnel',
            event_option2: 'onboarding;company, website link, subdomain',
        });
        setErrors((prev: Partial<CompanyFormDataType>) =>
            Object.fromEntries(
                Object.entries(prev).filter(([key]: [key: string, value: unknown]) => key !== fieldName),
            ),
        );
    };

    // One field on this step (Vova 29.10.2026): the affiliate portal address, the only thing not changeable later.
    // The server still requires a company name and a website, so both come from it: the name capitalised, the website
    // the portal itself — a placeholder that sends nobody to someone else's site. The product's Getting Started Guide
    // asks for the program's real name and landing page.
    const handleSubmit = async ({ subdomain }: Pick<CompanyFormDataType, 'subdomain'>) => {
        const formData: CompanyFormDataType = {
            subdomain,
            companyName: subdomain.charAt(0).toUpperCase() + subdomain.slice(1),
            companyLink: `https://${subdomain}.tapfiliate.com`,
        };
        try {
            const uuid = storage.read('stepUuid');
            const res = await submitCompanyForm({ body: formData, id: uuid });

            if (res?.error) {
                const errors = (res?.error?.data?.errors as CompanyFormDataType) || {};
                ga4event({
                    event_name: 'send_advertiser_create_account_error_f',
                    event_option1: 'advertiser registration funnel',
                    event_option2: 'onboarding;company, website link, subdomain',
                    event_option4: `error ${Object.values(errors).join(' ')}`,
                });
                setErrors(errors);
                if (Object.keys(errors).length) pointAt('subdomain');
            } else {
                ga4event({
                    event_name: 'send_advertiser_create_account_success_f',
                    event_option1: 'advertiser registration funnel',
                    event_option2: 'onboarding;company, website link, subdomain',
                });
                // Named after the step (setup/, "Set up your account"), not the company it no longer asks
                // (Vova 30.10.2026).
                signupEvent('signup:setup_submitted');

                storage.write('currentStep', 'TrialStep');
                storage.write('stepUuid', res?.data?.uuid || '');
                storage.write('companyStepData', formData || {});
                handleStep(1);
            }
        } catch (err) {
            console.error('error: ', err);
        }
    };

    return (
        <Form
            initialValues={{ subdomain: storage.read('companyStepData', { subdomain: '' }).subdomain }}
            name="company-form"
            layout="vertical"
            onFinish={(values: Pick<CompanyFormDataType, 'subdomain'>) => {
                if (address.tooShort(values.subdomain)) {
                    address.setWrong(true);
                    pointAt('subdomain');
                    return;
                }
                handleSubmit(values);
            }}
            form={form}
            requiredMark={false}
            validateTrigger="onBlur"
        >
            {/* Checked and hinted as the password on step 1: the hint "At least 3 characters" always shown. */}
            <Form.Item
                label={<RequiredLabel text={t('company_form.tapfiliate_link_ready')} />}
                htmlFor="company-form_subdomain"
                className={shakeClass('subdomain')}
            >
                <Form.Item
                    name="subdomain"
                    noStyle
                >
                    <Input
                        size="large"
                        addonAfter=".tapfiliate.com"
                        placeholder="yourcompany"
                        status={address.wrong || serverError ? 'error' : undefined}
                        onBlur={(e) => {
                            handleChange(e);
                            address.onLeave(e.target.value);
                        }}
                        onChange={(e) => {
                            // Letters and digits only, as the server takes them.
                            const value = (e.target.value.toLocaleLowerCase().match(/[a-z0-9]+/g) || []).join('');
                            form.setFieldValue('subdomain', value);
                            handleChange(e);
                            address.onType(value);
                        }}
                    />
                </Form.Item>
                <FieldHint wrong={address.wrong || Boolean(serverError)}>
                    {serverError || t('company_form.address_too_short')}
                </FieldHint>
            </Form.Item>

            <Form.Item style={{ margin: 0 }}>
                <Button
                    block
                    type="primary"
                    htmlType="submit"
                    style={{ fontWeight: 600 }}
                    size="large"
                    loading={isLoading}
                >
                    {t('company_form.continue')}
                </Button>
            </Form.Item>
        </Form>
    );
}
